import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { verifyEmail, resendVerification } from "../../services/authService";
import type { ApiResponse } from "../../types/common";

interface VerificationResult {
  success: boolean;
  message: string;
}

const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [result, setResult] = useState<VerificationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState<VerificationResult | null>(
    null,
  );
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const hasVerified = useRef(false);

  const token = searchParams.get("token");
  const email = searchParams.get("email");

  useEffect(() => {
    // Prevent multiple API calls
    if (hasVerified.current) {
      return;
    }

    if (!token || !email) {
      setResult({
        success: false,
        message: t("auth.invalidVerificationLink"),
      });
      return;
    }

    hasVerified.current = true;
    setLoading(true);

    verifyEmail(token, email)
      .then((res: ApiResponse<string>) => {
        setResult({
          success: res.isSuccess,
          message:
            res.message ||
            (res.isSuccess
              ? t("auth.emailVerifiedSuccess")
              : t("auth.verificationFailed")),
        });

        // Start countdown and redirect if verification was successful
        if (res.isSuccess) {
          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate("/login");
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      })
      .catch(() => {
        setResult({
          success: false,
          message: t("auth.verificationFailed"),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, email]);

  const handleResendVerification = async () => {
    if (!email) {
      setResendStatus({ success: false, message: t("auth.emailAddressNotFound") });
      return;
    }

    setResendLoading(true);
    setResendStatus(null);
    try {
      const res = await resendVerification(email);
      setResendStatus({ success: res.isSuccess, message: res.message });
    } catch {
      setResendStatus({
        success: false,
        message: t("auth.resendVerificationFailed"),
      });
    } finally {
      setResendLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: 3, textAlign: "center" }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t("auth.emailVerification")}
      </Typography>

      {result && (
        <Alert severity={result.success ? "success" : "error"} sx={{ mb: 3 }}>
          {result.message}
        </Alert>
      )}

      {resendStatus && (
        <Alert
          severity={resendStatus.success ? "success" : "error"}
          sx={{ mb: 3 }}
        >
          {resendStatus.message}
        </Alert>
      )}

      {result?.success ? (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("auth.emailVerifiedSuccess")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("auth.redirectingToLogin", { seconds: countdown })}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/")}
            sx={{ mt: 2, mr: 2 }}
          >
            {t("auth.goToHomeNow")}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            href="/login"
            sx={{ mt: 2 }}
          >
            {t("auth.goToLogin")}
          </Button>
        </Box>
      ) : (
        <Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t("auth.resendingVerification")}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleResendVerification}
            disabled={resendLoading}
            sx={{ mt: 2 }}
          >
            {resendLoading ? t("auth.sending") : t("auth.resendVerificationEmail")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default VerifyEmail;
